import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import { adminService } from '../../../services/adminService';
import { catalogService } from '../../../services/catalogService';

const ShipmentItemsSection = ({ shipmentId, items, onUpdate, shipment }) => {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [catalogItems, setCatalogItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // ✅ NEW: Track which row is in edit mode
  const [editingItemId, setEditingItemId] = useState(null);
  const [editFormData, setEditFormData] = useState({ catalogItemId: '', quantity: 1 });
  
  // ✅ NEW: Discount editing state
  const [localDiscount, setLocalDiscount] = useState(0);
  const [isEditingDiscount, setIsEditingDiscount] = useState(false);

  // ✅ Initialize discount from shipment prop
  useEffect(() => {
    if (shipment?.discount_amount !== undefined) {
      setLocalDiscount(parseFloat(shipment?.discount_amount) || 0);
    }
  }, [shipment?.discount_amount]);

  const loadCatalogItems = async () => {
    try {
      const data = await catalogService?.getAll();
      setCatalogItems(data);
    } catch (err) {
      console.error('Error loading catalog:', err);
    }
  };

  // ✅ MODIFIED: Simplified handleAddItem - now just inserts the item
  const handleAddItem = async () => {
    if (!selectedItem) {
      alert('Veuillez sélectionner un article');
      return;
    }

    try {
      setLoading(true);
      await adminService?.addShipmentItem(shipmentId, selectedItem, quantity);
      
      // Reset form
      setIsAddingItem(false);
      setSelectedItem('');
      setQuantity(1);
      
      // Refresh data
      onUpdate();
    } catch (err) {
      console.error('Error adding item:', err);
      alert(err?.message || 'Erreur lors de l\'ajout de l\'article');
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Cancel adding item
  const handleCancelAddItem = () => {
    setIsAddingItem(false);
    setSelectedItem('');
    setQuantity(1);
  };

  // ✅ NEW: Start adding item - load catalog if needed
  const handleStartAddItem = () => {
    setIsAddingItem(true);
    if (catalogItems?.length === 0) {
      loadCatalogItems();
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article?')) return;

    try {
      setLoading(true);
      await adminService?.removeShipmentItem(itemId);
      onUpdate();
    } catch (err) {
      console.error('Error removing item:', err);
      alert(err?.message || 'Erreur lors de la suppression de l\'article');
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Start editing a row
  const handleStartEdit = (item) => {
    setEditingItemId(item?.id);
    setEditFormData({
      catalogItemId: item?.catalogItemId,
      quantity: item?.quantity
    });
    // Load catalog items if not already loaded
    if (catalogItems?.length === 0) {
      loadCatalogItems();
    }
  };

  // ✅ NEW: Cancel editing
  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditFormData({ catalogItemId: '', quantity: 1 });
  };

  // ✅ NEW: Save edited item
  const handleSaveEdit = async (itemId) => {
    try {
      setLoading(true);
      await adminService?.updateShipmentItem(
        itemId,
        editFormData?.catalogItemId,
        editFormData?.quantity
      );
      setEditingItemId(null);
      setEditFormData({ catalogItemId: '', quantity: 1 });
      onUpdate();
    } catch (err) {
      console.error('Error updating item:', err);
      alert(err?.message || 'Erreur lors de la modification de l\'article');
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Save discount
  const handleSaveDiscount = async () => {
    try {
      setLoading(true);
      await adminService?.updateShipmentDiscount(shipmentId, localDiscount);
      setIsEditingDiscount(false);
      onUpdate();
      alert('✓ Remise mise à jour avec succès');
    } catch (err) {
      console.error('Error updating discount:', err);
      alert(err?.message || 'Erreur lors de la mise à jour de la remise');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    return items?.reduce((sum, item) => sum + parseFloat(item?.priceTotal || 0), 0);
  };

  const calculateFinalTotal = () => {
    const subtotal = calculateTotal();
    const discount = parseFloat(localDiscount) || 0;
    return Math.max(subtotal - discount, 0);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h3 className="text-xl font-bold text-gray-900">Articles & Prix</h3>
        <button
          onClick={handleStartAddItem}
          className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 shadow-sm"
          disabled={loading || isAddingItem}
        >
          <Icon name="Plus" size={16} />
          <span>Ajouter un article</span>
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantité</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix unitaire</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* ✅ NEW: Inline "Add Item" Row - appears at the top when adding */}
            {isAddingItem && (
              <tr className="bg-green-50 border-2 border-green-300">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  <select
                    value={selectedItem}
                    onChange={(e) => setSelectedItem(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    disabled={loading}
                  >
                    <option value="">Sélectionner un article</option>
                    {catalogItems?.map((item) => (
                      <option key={item?.id} value={item?.id}>
                        {item?.name} - {item?.price}€
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {selectedItem && catalogItems?.find(c => c?.id === selectedItem)?.category}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e?.target?.value) || 1)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                    disabled={loading}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {selectedItem ? `${parseFloat(catalogItems?.find(c => c?.id === selectedItem)?.price || 0)?.toFixed(2)} €` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {selectedItem ? `${(parseFloat(catalogItems?.find(c => c?.id === selectedItem)?.price || 0) * quantity)?.toFixed(2)} €` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleAddItem}
                      disabled={loading || !selectedItem}
                      className="text-green-600 hover:text-green-900 flex items-center space-x-1 disabled:opacity-50"
                    >
                      <Icon name="Check" size={16} />
                      <span>Enregistrer</span>
                    </button>
                    <button
                      onClick={handleCancelAddItem}
                      disabled={loading}
                      className="text-gray-600 hover:text-gray-900 flex items-center space-x-1"
                    >
                      <Icon name="X" size={16} />
                      <span>Annuler</span>
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {items?.map((item) => {
              const isEditing = editingItemId === item?.id;
              
              return (
                <tr key={item?.id} className={isEditing ? 'bg-blue-50' : ''}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {isEditing ? (
                      <select
                        value={editFormData?.catalogItemId}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, catalogItemId: e?.target?.value }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                      >
                        {catalogItems?.map((catItem) => (
                          <option key={catItem?.id} value={catItem?.id}>
                            {catItem?.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      item?.itemName
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {isEditing ? (
                      catalogItems?.find(c => c?.id === editFormData?.catalogItemId)?.category || item?.category
                    ) : (
                      item?.category
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {isEditing ? (
                      <input
                        type="number"
                        min="1"
                        value={editFormData?.quantity}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, quantity: parseInt(e?.target?.value) || 1 }))}
                        className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                      />
                    ) : (
                      item?.quantity
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {isEditing ? (
                      `${parseFloat(catalogItems?.find(c => c?.id === editFormData?.catalogItemId)?.price || item?.priceUnit)?.toFixed(2)} €`
                    ) : (
                      `${parseFloat(item?.priceUnit)?.toFixed(2)} €`
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {isEditing ? (
                      `${(parseFloat(catalogItems?.find(c => c?.id === editFormData?.catalogItemId)?.price || item?.priceUnit) * editFormData?.quantity)?.toFixed(2)} €`
                    ) : (
                      `${parseFloat(item?.priceTotal)?.toFixed(2)} €`
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {isEditing ? (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleSaveEdit(item?.id)}
                          disabled={loading}
                          className="text-green-600 hover:text-green-900 flex items-center space-x-1"
                        >
                          <Icon name="Check" size={16} />
                          <span>Enregistrer</span>
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={loading}
                          className="text-gray-600 hover:text-gray-900 flex items-center space-x-1"
                        >
                          <Icon name="X" size={16} />
                          <span>Annuler</span>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleStartEdit(item)}
                          disabled={loading}
                          className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                        >
                          <Icon name="Edit" size={16} />
                          <span>Modifier</span>
                        </button>
                        <button
                          onClick={() => handleRemoveItem(item?.id)}
                          disabled={loading}
                          className="text-red-600 hover:text-red-900 flex items-center space-x-1"
                        >
                          <Icon name="Trash2" size={16} />
                          <span>Supprimer</span>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50">
            <tr>
              <td colSpan="4" className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                Total articles:
              </td>
              <td className="px-6 py-4 text-sm font-bold text-gray-900">
                {calculateTotal()?.toFixed(2)} €
              </td>
              <td></td>
            </tr>
            <tr>
              <td colSpan="4" className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                Remise:
              </td>
              <td className="px-6 py-4 text-sm">
                {isEditingDiscount ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={localDiscount}
                      onChange={(e) => setLocalDiscount(parseFloat(e?.target?.value) || 0)}
                      className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                    />
                    <span className="text-gray-900">€</span>
                  </div>
                ) : (
                  <span className="text-red-600 font-bold">-{localDiscount?.toFixed(2)} €</span>
                )}
              </td>
              <td className="px-6 py-4 text-sm">
                {isEditingDiscount ? (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleSaveDiscount}
                      disabled={loading}
                      className="text-green-600 hover:text-green-900 flex items-center space-x-1"
                    >
                      <Icon name="Check" size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingDiscount(false);
                        setLocalDiscount(parseFloat(shipment?.discount_amount) || 0);
                      }}
                      disabled={loading}
                      className="text-gray-600 hover:text-gray-900 flex items-center space-x-1"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsEditingDiscount(true)}
                    disabled={loading}
                    className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                  >
                    <Icon name="Edit" size={16} />
                    <span>Modifier</span>
                  </button>
                )}
              </td>
            </tr>
            <tr className="bg-gray-100">
              <td colSpan="4" className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                Total à payer:
              </td>
              <td className="px-6 py-4 text-sm font-bold text-primary-600">
                {calculateFinalTotal()?.toFixed(2)} €
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default ShipmentItemsSection;